import React, { useState, useEffect, useMemo } from 'react';
import GraficoBarras from './GraficoBarras';

const API_BASE = '/api/dashboard';

export default function AbaEquipes({ competencia }) {
  const [ranking, setRanking] = useState([]);
  const [resumo, setResumo] = useState(null);
  const [filtros, setFiltros] = useState({ tipo: 'todos', regiao: 'todas' });
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState(null);

  useEffect(() => {
    if (!competencia) return;
    carregarDados();
  }, [competencia, filtros.tipo, filtros.regiao]);

  async function carregarDados() {
    setCarregando(true);
    setErro(null);
    try {
      const params = new URLSearchParams({
        competencia,
        tipo: filtros.tipo,
        regiao: filtros.regiao,
      });
      const resp = await fetch(`${API_BASE}/equipes?${params}`);
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const data = await resp.json();
      setRanking(data.ranking || []);
      setResumo(data.resumo || null);
    } catch (e) {
      setErro(e.message);
    } finally {
      setCarregando(false);
    }
  }

  // Cópias ordenadas separadas para cada gráfico (evita mutação do estado)
  const rankingPorTotal = useMemo(
    () => [...ranking].sort((a, b) => b.total_us - a.total_us),
    [ranking]
  );

  const rankingPorMedia = useMemo(
    () => [...ranking].sort((a, b) => b.media_diaria - a.media_diaria),
    [ranking]
  );

  const fmt2 = (v) => (v ?? 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 });

  if (carregando) {
    return <div style={{ color: 'var(--muted)', padding: '40px', textAlign: 'center' }}>Carregando equipes...</div>;
  }

  if (erro) {
    return <div style={{ color: 'var(--red)', padding: '40px', textAlign: 'center' }}>Erro: {erro}</div>;
  }

  return (
    <div>
      {/* Filtros */}
      <div style={{
        display: 'flex',
        gap: '16px',
        marginBottom: '24px',
        alignItems: 'center',
        flexWrap: 'wrap',
      }}>
        <div>
          <label style={{ fontSize: 'var(--font-xs)', color: 'var(--dim)', textTransform: 'uppercase', letterSpacing: '0.05em', marginRight: '8px' }}>
            Tipo
          </label>
          <select
            value={filtros.tipo}
            onChange={(e) => setFiltros(f => ({ ...f, tipo: e.target.value }))}
            style={{
              background: 'var(--surface)',
              border: '1px solid var(--border)',
              borderRadius: 'var(--radius-sm)',
              color: 'var(--text)',
              padding: '6px 12px',
              fontSize: 'var(--font-sm)',
              cursor: 'pointer',
            }}
          >
            <option value="todos">Todos</option>
            <option value="D">Dupla</option>
            <option value="M">Moto</option>
            <option value="U">Umpla</option>
            <option value="T">Trio</option>
            <option value="Q">Quarteto</option>
          </select>
        </div>

        <div>
          <label style={{ fontSize: 'var(--font-xs)', color: 'var(--dim)', textTransform: 'uppercase', letterSpacing: '0.05em', marginRight: '8px' }}>
            Região
          </label>
          <select
            value={filtros.regiao}
            onChange={(e) => setFiltros(f => ({ ...f, regiao: e.target.value }))}
            style={{
              background: 'var(--surface)',
              border: '1px solid var(--border)',
              borderRadius: 'var(--radius-sm)',
              color: 'var(--text)',
              padding: '6px 12px',
              fontSize: 'var(--font-sm)',
              cursor: 'pointer',
            }}
          >
            <option value="todas">Todas</option>
            <option value="AL">AL</option>
            <option value="RA">RA</option>
          </select>
        </div>

        {/* Resumo rápido */}
        {resumo && (
          <div style={{
            marginLeft: 'auto',
            display: 'flex',
            gap: '20px',
            fontSize: 'var(--font-sm)',
            color: 'var(--muted)',
          }}>
            <span><strong style={{ color: 'var(--text)' }}>{resumo.total_equipes}</strong> equipes</span>
            <span>US: <strong style={{ color: 'var(--amber)' }}>{fmt2(resumo.us_total_geral)}</strong></span>
            <span>Média: <strong style={{ color: 'var(--amber)' }}>{fmt2(resumo.media_geral)}</strong></span>
          </div>
        )}
      </div>

      {/* Gráficos lado a lado */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: '24px',
        alignItems: 'start',
      }}>
        {/* Esquerda: Ranking por US Total */}
        <div style={{
          background: 'var(--surface)',
          border: '1px solid var(--border)',
          borderRadius: 'var(--radius-md)',
          padding: '20px',
        }}>
          <div style={{
            fontSize: 'var(--font-sm)',
            fontWeight: 600,
            color: 'var(--amber)',
            marginBottom: '16px',
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
          }}>
            Ranking por US Total
          </div>
          <GraficoBarras
            data={rankingPorTotal}
            layout="horizontal"
            labelKey="numvei"
            valueKey="total_us"
            tooltipKeys={['nome', 'tipo_turma', 'regiao', 'dias_trabalhados', 'media_diaria']}
            tooltipLabels={{ nome: 'Eletricista', tipo_turma: 'Tipo', regiao: 'Região', dias_trabalhados: 'Dias', media_diaria: 'Média' }}
            formatter={(v) => v.toFixed(2)}
            height={600}
            emptyMessage="Nenhuma equipe encontrada"
          />
        </div>

        {/* Direita: Ranking por Média Diária */}
        <div style={{
          background: 'var(--surface)',
          border: '1px solid var(--border)',
          borderRadius: 'var(--radius-md)',
          padding: '20px',
        }}>
          <div style={{
            fontSize: 'var(--font-sm)',
            fontWeight: 600,
            color: 'var(--amber)',
            marginBottom: '16px',
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
          }}>
            Ranking por Média Diária
          </div>
          <GraficoBarras
            data={rankingPorMedia}
            layout="horizontal"
            labelKey="numvei"
            valueKey="media_diaria"
            tooltipKeys={['nome', 'tipo_turma', 'regiao', 'dias_trabalhados', 'total_us']}
            tooltipLabels={{ nome: 'Eletricista', tipo_turma: 'Tipo', regiao: 'Região', dias_trabalhados: 'Dias', total_us: 'Total' }}
            formatter={(v) => v.toFixed(2)}
            height={600}
            emptyMessage="Nenhuma equipe encontrada"
          />
        </div>
      </div>
    </div>
  );
}
